"use client";

import { useState } from "react";
import { Boxes, Cpu, Hammer, Sparkles, Truck, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = [{
  id: 1,
  name: "Generadores",
  icon: Sparkles,
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/generator_diesel_heavy_duty.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/generator_portable_gasoline.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/generator_industrial_three_phase.png"]
}, {
  id: 2,
  name: "Compresores",
  icon: Cpu,
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/compressor_rotary_screw_industrial.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/compressor_portable_air_tank.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/compressor_high_pressure_mining.png"]
}, {
  id: 3,
  name: "Herramientas",
  icon: Hammer,
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/tools_power_drill_kit.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/tools_demolition_hammer.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/tools_welding_equipment_set.png"]
}, {
  id: 4,
  name: "Maquinaria Pesada",
  icon: Truck,
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/excavator_caterpillar_mining.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/bulldozer_construction_site.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/crane_tower_heavy_lift.png"]
}, {
  id: 5,
  name: "Manejo de Materiales",
  icon: Boxes,
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/forklift_warehouse_electric.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/pallet_jack_industrial_hydraulic.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/conveyor_belt_system_modular.png"]
}, {
  id: 6,
  name: "Equipos Especializados",
  icon: Wrench,
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/drilling_rig_deep_bore.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/concrete_pump_truck_boom.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/road_roller_vibratory_compactor.png"]
}];
export default function OurEquipmentRange() {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: linkRef,
    isVisible: linkVisible
  } = useRevealOnScroll({
    threshold: 0.8,
    once: true
  });
  const handleCardClick = (categoryId: number) => {
    setSelectedCategory(categoryId);
    setIsSheetOpen(true);
  };
  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
  return <section ref={sectionRef} className="relative min-h-[30vh] bg-dark-background text-foreground-dark py-16 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        <h2 className="text-4xl md:text-5xl font-display font-bold text-center mb-12 text-foreground-dark">
          Nuestra Gama de Equipos
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, i) => {
          const Icon = category.icon;
          return <div key={category.id} data-index={i}>
                <Card className={`cursor-pointer bg-dark-background/60 border border-muted-dark hover:shadow-strong hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${sectionVisible ? i === 0 ? "animate-fade-in-up" : i === 1 ? "animate-fade-in-up-delay-100" : "animate-fade-in-up-delay-200" : "opacity-0 translate-y-4"}`} onClick={() => handleCardClick(category.id)}>
                  <CardHeader className="items-center text-center">
                    <div className="mb-3 p-3 rounded-md bg-primary/10 border border-primary/30">
                      <Icon className="size-8 text-primary" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-xl text-foreground-dark">{category.name}</CardTitle>
                  </CardHeader>
                </Card>

                {i < categories.length - 1 && <Separator className={`my-4 bg-muted-dark transition-opacity duration-[var(--duration-slow)] ${sectionVisible ? "opacity-100" : "opacity-0"}`} />}
              </div>;
        })}
        </div>

        <div ref={linkRef} className={`mt-12 text-center transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${linkVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
          <Link to="/EquipmentPage" className="inline-flex items-center gap-2 text-primary hover:text-primary-hover underline-offset-4 hover:underline font-semibold transition-colors duration-[var(--duration-fast)]">
            Ver Catálogo Completo
          </Link>
        </div>
      </div>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[70vh] bg-background/95 backdrop-blur-md border-t border-border-strong">
          <SheetHeader>
            <SheetTitle className="text-2xl font-display text-foreground">
              {selectedCategoryData?.name}
            </SheetTitle>
          </SheetHeader>

          {selectedCategoryData && <div className="mt-6 px-8">
              <Carousel opts={{
            align: "start",
            loop: true
          }} className="w-full max-w-4xl mx-auto">
                <CarouselContent>
                  {selectedCategoryData.images.map((img, idx) => <CarouselItem key={idx} data-index={idx} className="md:basis-1/2 lg:basis-1/3">
                      <Card className="border border-border shadow-soft hover:shadow-medium transition-shadow duration-[var(--duration-medium)]">
                        <CardContent className="flex items-center justify-center p-0">
                          <img src={img} alt={`${selectedCategoryData.name} producto ${idx + 1}`} className="w-full h-48 object-cover rounded-t-md hover:scale-105 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)]" />
                        </CardContent>
                      </Card>
                    </CarouselItem>)}
                </CarouselContent>
                <CarouselPrevious className="left-0 -translate-x-12" />
                <CarouselNext className="right-0 translate-x-12" />
              </Carousel>
            </div>}
        </SheetContent>
      </Sheet>
    </section>;
}